import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faRadar } from "@fortawesome/pro-solid-svg-icons/faRadar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import User from "@mapmycustomers/shared/types/User";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";

import ButtonLink from "@app/component/ButtonLink";
import { selectUser, toggleLayerVisibility } from "@app/scene/map/store/layers/actions";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";

import LegendCard, { BaseLegendCardProps } from "../LegendCard";

import UserLocationList from "./component/UserLocationList";
import styles from "./UserLocationsLegendCard.module.scss";

interface Props extends BaseLegendCardProps {
  onSelectUser: typeof selectUser;
  selectedUser?: User;
  toggleLayerVisibility: typeof toggleLayerVisibility;
}

const UserLocationLegendCard: React.FC<Props> = ({
  onSelectUser,
  selectedUser,
  toggleLayerVisibility,
  ...props
}) => {
  const intl = useIntl();
  const analytics = useExtendedAnalytics("User Location", useAnalytics());

  const handleVisibilityChange = useCallback(
    (visible) => {
      analytics.clicked([visible ? "On" : "Off"]);
      toggleLayerVisibility({ name: "userLocations", visible });
      if (!visible) {
        onSelectUser(undefined);
      }
    },
    [analytics, onSelectUser, toggleLayerVisibility]
  );

  useChangeTracking(() => {
    if (props.active) {
      analytics.clicked([]);
    }
  }, [analytics, props.active]);

  useChangeTracking(() => {
    if (props.active) {
      analytics.clicked([]);
    }
  }, [analytics, props.active]);

  return (
    <LegendCard
      {...props}
      icon={<FontAwesomeIcon icon={faRadar} />}
      onVisibilityChange={handleVisibilityChange}
      title={intl.formatMessage({
        id: "map.layer.userLocation.legend",
        defaultMessage: "User Locations",
        description: "Title of the User Location legend",
      })}
    >
      {props.visible ? (
        <UserLocationList />
      ) : (
        <div className={styles.disabledContainer}>
          <FontAwesomeIcon className={styles.icon} icon={faRadar} size="4x" />
          <div className={styles.disabledDescription}>
            {intl.formatMessage({
              id: "map.layer.userLocation.disabled.description",
              defaultMessage: "See current locations of your field team and their location history",
              description: "Disabled User Location legend - description",
            })}
          </div>
          <ButtonLink onClick={() => handleVisibilityChange(true)}>
            {intl.formatMessage({
              id: "map.layer.userLocation.disabled.enableLegend",
              defaultMessage: "Enable User Locations",
              description: "Disabled User Location legend - Enable User Locations",
            })}
          </ButtonLink>
        </div>
      )}
    </LegendCard>
  );
};

const mapDispatchToProps = {
  onSelectUser: selectUser,
  toggleLayerVisibility,
};

export default connect(null, mapDispatchToProps)(UserLocationLegendCard);
